import { User } from "../components/types/User";
import { whenIdle } from "./whenIdle";

/**
 * PostHog is lazy-loaded so the heavy `posthog-js` runtime (~750KB of source,
 * a large slice of the entry bundle) is split into its own chunk and fetched
 * once the browser is idle. This keeps first paint / TTI fast on mobile
 * without losing analytics behavior.
 *
 * Behavior notes:
 * - Events/calls that arrive before the dynamic import resolves are queued
 *   and flushed in order once `posthog` is available, so no captures are lost.
 * - A page waiting on feature flags skips the idle wait (see
 *   {@link onFeatureFlagsReady}). The hero only waits 700ms for its experiments,
 *   so waiting for idle there would quietly drop visitors out of those tests.
 * - `isFeatureEnabled` returns `false` until the chunk is loaded. This matches
 *   the previous eager behavior on first paint: PostHog's flag fetch is itself
 *   async, so first-time visitors already got the default (control) variant
 *   for the homepage hero A/B test until flags resolved. The hero renders once
 *   and does not re-evaluate the flag on flag-arrival, so first-paint variant
 *   selection is unchanged.
 *
 * All public exports keep their existing signatures so callers are unchanged.
 */

type AnalyticsProperties = Record<
  string,
  string | number | boolean | null | undefined
>;

type PostHogLike = {
  init: (token: string, opts: Record<string, unknown>) => void;
  capture: (event: string, properties?: AnalyticsProperties) => void;
  isFeatureEnabled: (flagKey: string) => boolean | undefined;
  getFeatureFlag: (flagKey: string) => string | boolean | undefined;
  onFeatureFlags: (cb: () => void) => void;
  identify: (distinctId: string, properties?: AnalyticsProperties) => void;
  reset: () => void;
};

const token = process.env.REACT_APP_POSTHOG_KEY;
const host =
  process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com";

let initialized = false;
let posthog: PostHogLike | null = null;

type QueuedCall =
  | { type: "capture"; event: string; properties?: AnalyticsProperties }
  | { type: "identify"; distinctId: string; properties?: AnalyticsProperties }
  | { type: "reset" };

const queue: QueuedCall[] = [];

const flushQueue = () => {
  if (!posthog) return;
  while (queue.length > 0) {
    const call = queue.shift()!;
    if (call.type === "capture") {
      posthog.capture(call.event, call.properties);
    } else if (call.type === "identify") {
      posthog.identify(call.distinctId, call.properties);
    } else if (call.type === "reset") {
      posthog.reset();
    }
  }
};

let loadStarted = false;

const loadPostHog = () => {
  if (loadStarted || !token) return;
  loadStarted = true;

  // Dynamic import splits posthog-js into a separate async chunk.
  import("posthog-js")
    .then((mod) => {
      const ph = (mod.default || mod) as PostHogLike;
      posthog = ph;
      ph.init(token, {
        api_host: host,
        autocapture: true,
        capture_pageview: false,
        persistence: "localStorage+cookie",
        person_profiles: "identified_only",
        // Error tracking: capture uncaught JS exceptions with stack traces as
        // $exception events. This is the single richest source of code-actionable
        // signal for the growth agent — a real error on a real route maps straight
        // to a file/line fix.
        capture_exceptions: true,
        // Field Core Web Vitals (LCP/INP/CLS) + network timing as $web_vitals,
        // tied to real sessions. Complements PageSpeed's lab data with what users
        // actually experience (e.g. the poor INP/CLS seen in the Clarity widget).
        capture_performance: { network_timing: true, web_vitals: true },
        // Dead-click autocapture emits $dead_click; the scanner already looks for
        // these but the project was never capturing them.
        capture_dead_clicks: true,
        // Surface console errors inside replays so a recording carries its own
        // failure context when the agent (or a human) reviews it.
        enable_recording_console_log: true,
        session_recording: {
          maskAllInputs: true,
          maskTextSelector: ".ph-mask",
          blockSelector: ".ph-no-capture",
          // Was 0.1 (10%). At this site's volume (~90 sessions/week) a 10% sample
          // yields too few recordings to be useful for review or AI summaries —
          // 30 days of data showed zero usable recordings. Record everything; the
          // volume is trivial and full coverage is what makes replays actionable.
          sampleRate: 1.0,
        },
      });
      flushQueue();
      ph.onFeatureFlags(() => {
        flagsLoaded = true;
        flagListeners.splice(0).forEach((cb) => cb());
      });
    })
    .catch((err) => {
      // Don't let analytics chunk failure break the app.
      console.error("[posthog] failed to load analytics chunk:", err);
    });
};

export const initPostHog = () => {
  if (initialized || !token || typeof window === "undefined") {
    return;
  }
  // Set now, not on load: calls queue from here on, so pageviews and identify
  // made during the idle wait reach PostHog in order.
  initialized = true;
  whenIdle(loadPostHog);
};

export const capture = (event: string, properties?: AnalyticsProperties) => {
  if (!initialized) {
    return;
  }
  if (posthog) {
    posthog.capture(event, properties);
  } else {
    queue.push({ type: "capture", event, properties });
  }
};

export const isFeatureEnabled = (flagKey: string) => {
  if (!initialized || !posthog) {
    return false;
  }
  return posthog.isFeatureEnabled(flagKey) === true;
};

/** Multivariate flag value ("control", "treatment_x", ...), or undefined
 *  until PostHog and its flags have loaded. Reading it records exposure. */
export const getFeatureFlagVariant = (flagKey: string): string | undefined => {
  if (!initialized || !posthog) return undefined;
  const value = posthog.getFeatureFlag(flagKey);
  return typeof value === "string" ? value : undefined;
};

const flagListeners: Array<() => void> = [];
let flagsLoaded = false;

/** Runs `cb` once flags are available (immediately if they already are).
 *  Never fires when PostHog is not configured, so callers need a timeout. */
export const onFeatureFlagsReady = (cb: () => void) => {
  if (flagsLoaded) {
    cb();
    return;
  }
  flagListeners.push(cb);
  if (initialized) loadPostHog();
};

export const capturePageview = (path: string) => {
  capture("$pageview", {
    $current_url: window.location.href,
    path,
  });
};

export const identifyUser = (user: User) => {
  if (!initialized || !user?.id) {
    return;
  }
  const props: AnalyticsProperties = {
    email: user.email || undefined,
    role: user.role ? String(user.role) : undefined,
    customer_id: user.customer_id || undefined,
  };
  if (posthog) {
    posthog.identify(String(user.id), props);
  } else {
    queue.push({ type: "identify", distinctId: String(user.id), properties: props });
  }
};

export const identifyLead = (
  email: string,
  properties?: AnalyticsProperties
) => {
  if (!initialized || !email) {
    return;
  }
  // Preserve the anonymous session until account identification. Identifying by email
  // created a second person when the same visitor later identified with a user ID.
  capture("lead_identified", { ...properties, measurement_version: 2 });
};

export const resetPostHog = () => {
  if (!initialized) {
    return;
  }
  if (posthog) {
    posthog.reset();
  } else {
    queue.push({ type: "reset" });
  }
};
